// Typed donor endpoint functions. Backed by GET /api/v1/donors/*.
//
// Per CHG-1225 plan §1.1. Consumed by @generated/screens/donor-list.ts +
// donor-detail.ts queryFn callbacks.

import { apiClient } from "./client";
import {
  donorSortParam,
  listPageParams,
  normalizeListResponse,
  type NormalizedList,
  type RawListEnvelope,
} from "./list-contract";
import type {
  Donor,
  DonorStats,
  TimelineEvent,
  RecurringDonationSummary,
  PledgeSummary,
  DeliveryLogEntry,
  DonorPreferences,
  PagedEnvelope,
} from "./types";

export interface ListDonorsParams {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortDirection?: "asc" | "desc";
  search?: string;
  // CHG-1666 / ITER-3778 — opaque filter dict from the v2 ListPage
  // filterChips slot. Keys match the entity-hint filter_chip[].field.
  // CHG-2916 / ITER-905655 — these were forwarded verbatim, but the
  // live /donors router (src/api/app/routers/donors.py:list_donors)
  // does NOT read `lifecycle_stage`, `days_since_last_gift`, or
  // `has_recurring` — it silently drops them, so the chips were no-ops.
  // listDonors now maps the ONE chip the BE actually backs
  // (lifecycle_stage → the router's `stage: DonorStage` param, for the
  // 3 chip values that are valid DonorStage members) and DROPS the
  // unbacked keys rather than sending dead params. See FILTER mapping
  // in the body + the entity-hint note for the hide-until-backed
  // follow-up.
  filters?: Record<string, string | number | boolean | undefined>;
}

// CHG-2916 — donor list-filter chip → live-router param mapping.
//
// The /donors router accepts `stage: DonorStage` (prospect | first_time |
// active | at_risk | lapsed | reactivated | recurring | vip). The
// `lifecycle_stage` filter chips in donor.entity-hint.yaml emit values
// active / at_risk / failed / lapsed. Three of those are valid
// DonorStage members and map cleanly onto `stage`; `failed` is NOT a
// DonorStage value, so that chip is dropped (it would 422). The other
// two chip groups (`days_since_last_gift`, `has_recurring`) have NO
// backing router param at all and are dropped here — FOLLOW-UP: hide
// those chips in the entity-hint until the BE adds the params (noted on
// the spec; can't edit the generated screen file, and dropping the
// dead param is the FE-only equivalent of "no broken filter / no 422").
const DONOR_STAGE_VALUES = new Set([
  "prospect",
  "first_time",
  "active",
  "at_risk",
  "lapsed",
  "reactivated",
  "recurring",
  "vip",
]);

function mapDonorFilters(
  filters: Record<string, string | number | boolean | undefined> | undefined,
): Record<string, string> {
  const out: Record<string, string> = {};
  if (!filters) return out;
  // lifecycle_stage chip → router `stage` param (valid DonorStage only).
  const lifecycle = filters.lifecycle_stage;
  if (typeof lifecycle === "string" && DONOR_STAGE_VALUES.has(lifecycle)) {
    out.stage = lifecycle;
  }
  // status / giving_level / segment_id are real router params — forward
  // verbatim if a future chip emits them.
  for (const key of ["status", "giving_level", "segment_id"]) {
    const v = filters[key];
    if (v !== undefined && v !== null && v !== "") out[key] = String(v);
  }
  // Intentionally NOT forwarded (unbacked → dropped, see comment above):
  // days_since_last_gift, has_recurring.
  return out;
}

export async function listDonors(params: ListDonorsParams = {}): Promise<NormalizedList<Donor>> {
  const r = await apiClient().get<RawListEnvelope<Donor>>("/donors", {
    params: {
      ...listPageParams(params),
      // Strict DonorSortBy enum value (no `:desc` suffix). Unknown /
      // absent field → undefined so the router uses its own default
      // instead of 422-ing. Fixes the first-load 422.
      sort: donorSortParam(params.sortField),
      ...mapDonorFilters(params.filters),
    },
  });
  return normalizeListResponse<Donor>(r.data);
}

export async function getDonor(id: string): Promise<Donor> {
  const r = await apiClient().get<Donor>(`/donors/${id}`);
  return r.data;
}

export async function getDonorStats(id: string): Promise<DonorStats> {
  const r = await apiClient().get<DonorStats>(`/donors/${id}/stats`);
  return r.data;
}

export async function getDonorTimeline(id: string): Promise<TimelineEvent[]> {
  const r = await apiClient().get<TimelineEvent[]>(`/donors/${id}/timeline`);
  return r.data;
}

export async function getDonorPledges(id: string): Promise<PagedEnvelope<PledgeSummary>> {
  const r = await apiClient().get<PagedEnvelope<PledgeSummary>>(`/donors/${id}/pledges`);
  return r.data;
}

export async function getDonorRecurring(id: string): Promise<PagedEnvelope<RecurringDonationSummary>> {
  const r = await apiClient().get<PagedEnvelope<RecurringDonationSummary>>(`/donors/${id}/recurring`);
  return r.data;
}

export async function getDonorDeliveryLogs(id: string): Promise<PagedEnvelope<DeliveryLogEntry>> {
  const r = await apiClient().get<PagedEnvelope<DeliveryLogEntry>>(`/donors/${id}/delivery-logs`);
  return r.data;
}

export async function getDonorPreferences(id: string): Promise<DonorPreferences> {
  const r = await apiClient().get<DonorPreferences>(`/donors/${id}/preferences`);
  return r.data;
}

export const donorsApi = {
  listDonors,
  getDonor,
  getDonorStats,
  getDonorTimeline,
  getDonorPledges,
  getDonorRecurring,
  getDonorDeliveryLogs,
  getDonorPreferences,
};
